// src/hooks/useEnsureEmployeeInfo.ts
import { useContext, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { WebAPIContext } from '../contexts/WebAPIContext';
import { setEmployeeInfo, Affiliation } from '../store/employeeSlice';

/** ログインユーザー情報（複数所属対応） */
export interface LoginUserInfo {
  userId: string;
  userName: string;
  userRole: string | null;
  isMasterMaintenanceAccessible: boolean;
  isHeadquartersManagement: boolean;
  isAIEnabled: boolean;
  affiliations: Affiliation[];
}

interface GetLoginUserInfoResponse {
  data: {
    getLoginUserInfo: LoginUserInfo;
  };
}

/**
 * ログインユーザー情報取得API（複数所属対応）を呼び出す
 * @param request WebAPIContextのrequest関数
 * @param email ログインユーザーのメールアドレス（userIdとして使用）
 * @returns ログインユーザー情報（取得失敗時はundefined）
 */
export const fetchLoginUserInfo = async (
  request: (payload: any) => Promise<any>,
  email: string
): Promise<LoginUserInfo | undefined> => {
  try {
    const result: GetLoginUserInfoResponse = await request({
      query: `
        query getLoginUserInfo($userId: String!) {
          getLoginUserInfo(userId: $userId) {
            userId
            userName
            userRole
            isMasterMaintenanceAccessible
            isHeadquartersManagement
            isAIEnabled
            affiliations {
              organizationId
              organizationName
              positionId
              positionName
              order
              parentOrganizationId
              descendantOrganizationClassIds
            }
          }
        }
      `,
      variables: { userId: email }
    });
    return result?.data?.getLoginUserInfo;
  } catch (error) {
    console.error("Error in getLoginUserInfo:", error);
    return undefined;
  }
};

/**
 * ログインユーザー情報をsetEmployeeInfo用のpayloadに変換する
 * @param loginUserInfo ログインユーザー情報
 * @returns setEmployeeInfoに渡すpayload
 */
export const toEmployeeInfoPayload = (loginUserInfo: LoginUserInfo) => {
  const primaryAffiliation = loginUserInfo.affiliations?.[0];
  return {
    employeeId: loginUserInfo.userId,
    employeeName: loginUserInfo.userName || "",
    divisionId: primaryAffiliation?.organizationId || "",
    divisionName: primaryAffiliation?.organizationName || "",
    parentOrganizationId: primaryAffiliation?.parentOrganizationId || "",
    descendantOrganizationClassIds: primaryAffiliation?.descendantOrganizationClassIds || [],
    affiliations: loginUserInfo.affiliations || [],
    userRole: loginUserInfo.userRole || null,
    isMasterMaintenanceAccessible: loginUserInfo.isMasterMaintenanceAccessible || false,
    isHeadquartersManagement: loginUserInfo.isHeadquartersManagement || false,
    isAIEnabled: loginUserInfo.isAIEnabled ?? false,
  };
};

/**
 * ProtectedRoute専用: Redux上にemployeeIdが未設定の場合のみログインユーザー情報を取得するフック
 * 別タブでマスタメンテナンス画面に直接アクセスした場合など、Home.tsxを経由せずに
 * employeeInfoが未取得のまま画面に到達したケースを救済する。
 * 既にemployeeIdが設定済みの場合はAPIを呼ばない（Home.tsx側の毎回再取得とは責務が異なる）。
 * @returns isLoading 取得中かどうか
 */
export const useEnsureEmployeeInfo = () => {
  const dispatch = useAppDispatch();
  const { request: WebAPIRequest } = useContext(WebAPIContext);
  const { email, isAuthorized } = useAppSelector(state => state.auth);
  const employeeIdInStore = useAppSelector(state => state.employee.employeeId);
  const [isLoading, setIsLoading] = useState(!employeeIdInStore);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!email || !isAuthorized) return;
    if (employeeIdInStore) {
      setIsLoading(false);
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    (async () => {
      try {
        const loginUserInfo = await fetchLoginUserInfo(WebAPIRequest, email);
        if (!loginUserInfo?.userId) {
          console.error("Employee ID is undefined");
          return;
        }
        dispatch(setEmployeeInfo(toEmployeeInfoPayload(loginUserInfo)));
      } finally {
        fetchingRef.current = false;
        setIsLoading(false);
      }
    })();
  }, [email, isAuthorized, employeeIdInStore, dispatch, WebAPIRequest]);

  return { isLoading };
};
